import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import "./ReportMessages.scss";
import { AgingReport, AgingReportBalance, AgingReportMessage, toMonthYear, toPrettyDate } from "common";
import Select, { StylesConfig } from 'react-select';
import { useUser } from "../store";
import { notify } from "../util/notify";
import ReportMessage from "./ReportMessage";
import { useReportUpdater } from "../store/reports";
import { useForm } from "react-hook-form"
import { useConfirmation } from "./ConfirmationProvider";
import { getThreadStatus } from "../util/alerts";
import ReplyComposer from "./ReplyComposer";



type PaymentInputs = {
  paidMonths: string[],
  paidDate: string,
  checkNumber: string,
}

const monthSelectStyles: StylesConfig<{ value: string; label: string }, true> = {
  menuPortal: (base) => ({ ...base, zIndex: 2000 })
};

const USERS_CACHE_MS = 5 * 60 * 1000;
// The "+" tab; real tab keys are comma-joined months, so it cannot clash
const NEW_THREAD_TAB = "+";
let cachedUsers: { users: any[]; fetchedAt: number } | undefined;

export default function ReportMessages({
  report,
  onCancel,
  docked = false,
  onDock,
}: {
  report: AgingReport;
  onCancel: () => void;
  /** Shown inside a docked chat window, which supplies its own header */
  docked?: boolean;
  /** Moves the conversation to a docked chat window */
  onDock?: () => void;
}) {
  const { invokeApi, navigate, user } = useUser();
  const confirm = useConfirmation();
  const updateReport = useReportUpdater();


  const [users, setUsers] = useState<any[]>([]);
  const [updatedReport, setUpdatedReport] = useState<AgingReport>(report);
  // A docked chat is handed the latest copy of the report as it refreshes, so it follows other people's messages
  useEffect(() => {
    setUpdatedReport(report);
  }, [report]);
  const [isPaymentFormOpen, setIsPaymentFormOpen] = useState(false);
  const [showArchivedMessages, setShowArchivedMessages] = useState(false);
  const [monthFilter, setMonthFilter] = useState<string | null>(null);

  const paymentMonthOptions = Object.entries(updatedReport?.balances ?? {})
    .filter(([, balance]) => (balance as AgingReportBalance)?.history?.[0] > 0)
    .map(([month]) => ({ value: month, label: toMonthYear(month) }));

  const paidMonths = Object.entries(updatedReport?.balances ?? {})
    .filter(([, balance]) => (balance as AgingReportBalance)?.paid)
    .sort(([firstMonth], [secondMonth]) => secondMonth.localeCompare(firstMonth));

  const {
    register: registerPayment,
    handleSubmit: handlePaymentSubmit,
    watch: watchPayment,
    setValue: setPaymentValue,
    reset: resetPayment,
    formState: { errors: paymentErrors },
  } = useForm<PaymentInputs>({
    defaultValues: {
      paidMonths: [],
      paidDate: '',
      checkNumber: '',
    }
  });

  const paymentMonths = watchPayment('paidMonths');
  const activeMessages = updatedReport?.messages?.filter((message) => !message.archivedAt) ?? [];
  const archivedMessages = updatedReport?.messages?.filter((message) => Boolean(message.archivedAt)) ?? [];

  useEffect(() => {
    const selectedBalances = paymentMonths.map((month) => updatedReport?.balances?.[month]);
    const firstBalance = selectedBalances[0];
    const hasMatchingPaymentDetails = selectedBalances.length > 0 && selectedBalances.every((balance) =>
      balance?.paidDate === firstBalance?.paidDate &&
      balance?.checkNumber === firstBalance?.checkNumber
    );
    setPaymentValue('paidDate', hasMatchingPaymentDetails ? firstBalance?.paidDate ?? '' : '');
    setPaymentValue('checkNumber', hasMatchingPaymentDetails ? firstBalance?.checkNumber ?? '' : '');
  }, [paymentMonths, updatedReport, setPaymentValue]);



  useEffect(() => {
    // The recipient list rarely changes, so reuse it between modal opens instead of a round trip each time
    if (cachedUsers && Date.now() - cachedUsers.fetchedAt < USERS_CACHE_MS) {
      setUsers(cachedUsers.users);
      return;
    }
    invokeApi({ endpoint: "/getUsersForNonAdmins" }).then((data: any) => {
      cachedUsers = { users: data, fetchedAt: Date.now() };
      setUsers(data);
    });
    // Once per open; the cache above covers the rest
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshReport = (report: AgingReport) => {
    setUpdatedReport(report);
    updateReport(report);
  }

  const monthOptions: { value: string; label: string }[] = [];
  if (updatedReport?.balances) {
    Object.entries(updatedReport.balances)?.forEach(([month, balance]) => {
      const { history } = balance as AgingReportBalance;
      if (history?.[0] === 0) return;
      monthOptions.push({
        value: month,
        label: toMonthYear(month)
      });
    });
  }

  const savePayment = async (input: PaymentInputs, successMessage: string, paid = true) => {
    if (paid && !input.paidDate) {
      notify.error('Pay date is required when marking a claim as paid.');
      return;
    }
    if (!input.paidMonths.length) {
      notify.error('Select at least one month.');
      return;
    }

    try {
      // One request for all the months: a single read and write on the server
      const report: AgingReport = await invokeApi({
        endpoint: '/toggleClaimPaid',
        data: {
          months: input.paidMonths,
          residentName: updatedReport.residentName,
          billType: updatedReport.billType,
          index: updatedReport.index,
          paid,
          paidDate: input.paidDate || undefined,
          checkNumber: input.checkNumber || undefined,
        },
      });
      refreshReport(report);
      setIsPaymentFormOpen(false);
      resetPayment({
        paidMonths: input.paidMonths,
        paidDate: input.paidDate,
        checkNumber: input.checkNumber,
      });
      notify.success(successMessage);
    } catch {
      // invokeApi already reports the failure
    }
  };

  const onSubmitPayment = (input: PaymentInputs) => {
    const monthLabels = input.paidMonths.map(toMonthYear).join(', ');
    savePayment(input, `Saved payment status for ${monthLabels}`);
  };

  const onRemovePaidStatus = (month: string) => {
    const monthLabel = toMonthYear(month);
    confirm({
      title: "Remove paid status?",
      message: `Remove the paid status and payment details for ${monthLabel}?`,
      confirmLabel: "Remove status",
    }).then((confirmed) => {
      if (!confirmed) return;
      savePayment(
        { paidMonths: [month], paidDate: '', checkNumber: '' },
        `Removed paid status for ${monthLabel}`,
        false
      );
    });
  };

  // A report's key is group#facility#payer#report
  const [reportGroup, facilitySlug, payerSlug] = (updatedReport.pk ?? "").split("#");
  const payerPath = payerSlug ? `/${reportGroup}/${facilitySlug}/${payerSlug}` : undefined;

  const threadStatuses = new Map(activeMessages.map((message) => [message.id, getThreadStatus(updatedReport, message, user?.username)]));
  const overdueCount = [...threadStatuses.values()].filter((status) => status.overdue).length;
  const newCount = [...threadStatuses.values()].filter((status) => status.isNew).length;

  // Threads with the same set of months share a tab; one without months goes under "" (General)
  const tabOf = (message: AgingReportMessage) => [...(message.months ?? [])].sort().join(",");
  const monthTabs = [...new Set(activeMessages.map(tabOf))]
    .sort((first, second) => {
      const [firstMonths, secondMonths] = [first.split(","), second.split(",")];
      return secondMonths[secondMonths.length - 1].localeCompare(firstMonths[firstMonths.length - 1]) || firstMonths.length - secondMonths.length;
    })
    .map((month) => {
      const threads = activeMessages.filter((message) => tabOf(message) === month);
      return {
        month,
        total: threads.length,
        paid: Boolean(month) && month.split(",").every((value) => (updatedReport?.balances?.[value] as AgingReportBalance | undefined)?.paid),
        overdue: threads.filter((message) => threadStatuses.get(message.id)?.overdue).length,
        fresh: threads.filter((message) => threadStatuses.get(message.id)?.isNew).length,
      };
    });
  const isNewThreadTab = monthFilter === NEW_THREAD_TAB;
  // Opens on a tab with an overdue thread, else on the tab of the most recent message or reply
  const lastActivity = (message: AgingReportMessage) =>
    [message.createdAt, ...(message.responses ?? []).filter((response) => !response.archivedAt).map((response) => response.createdAt)]
      .filter(Boolean)
      .sort()
      .pop() ?? "";
  const latestActivityTab = activeMessages.reduce<AgingReportMessage | undefined>(
    (latest, message) => (!latest || lastActivity(message) > lastActivity(latest) ? message : latest),
    undefined
  );
  const defaultMonth = monthTabs.find((tab) => tab.overdue > 0)?.month ?? (latestActivityTab ? tabOf(latestActivityTab) : monthTabs[0]?.month);
  const selectedMonth = isNewThreadTab ? NEW_THREAD_TAB : monthTabs.some((tab) => tab.month === monthFilter) ? monthFilter : defaultMonth;
  const tabId = (tab?: string | null) => (tab === NEW_THREAD_TAB ? "month-tab-new" : `month-tab-${tab || "na"}`);
  const visibleMessages = activeMessages.filter((message) => tabOf(message) === selectedMonth);
  const tabLabel = (tab: string) => {
    if (!tab) return "General";
    const months = tab.split(",").map((month) => toMonthYear(month));
    return months.length > 3 ? `${months[0]} +${months.length - 1}` : months.join(" + ");
  };

  const messageListRef = useRef<HTMLElement>(null);
  const chatBodyRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  // The tab row scrolls sideways, so bring the open tab into it (offsetLeft is relative to the sticky row)
  useEffect(() => {
    const row = tabsRef.current;
    const tab = row?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
    if (!row || !tab) return;
    const margin = 24;
    if (tab.offsetLeft - margin < row.scrollLeft) row.scrollLeft = Math.max(tab.offsetLeft - margin, 0);
    else if (tab.offsetLeft + tab.offsetWidth + margin > row.scrollLeft + row.clientWidth) row.scrollLeft = tab.offsetLeft + tab.offsetWidth + margin - row.clientWidth;
  }, [selectedMonth, monthTabs.length]);
  const displayNames = Object.fromEntries(users.filter((person) => person?.username).map((person) => [person.username, person.name || person.username]));

  // Like a chat, it opens at the latest message, and follows it when a new one arrives
  const messageCount = activeMessages.reduce((total, message) => total + 1 + (message.responses ?? []).filter((response) => !response.archivedAt).length, 0);
  const latestVisibleId = visibleMessages.reduce<AgingReportMessage | undefined>(
    (latest, message) => (!latest || lastActivity(message) >= lastActivity(latest) ? message : latest),
    undefined
  )?.id;
  useEffect(() => {
    const thread = latestVisibleId ? messageListRef.current?.querySelector<HTMLElement>(`[data-thread-id="${latestVisibleId}"]`) : null;
    const latest = [...(thread?.querySelectorAll<HTMLElement>(".response-card:not(.is-archived-response)") ?? [])].pop() ?? thread;
    latest?.scrollIntoView?.({ block: "end" });
  }, [messageCount, selectedMonth, latestVisibleId]);
  const jumpIndex = useRef({ overdue: 0, new: 0 });
  // The sticky tabs sit right under the sticky alerts bar, whose height changes as it wraps
  const hasAlerts = overdueCount > 0 || newCount > 0;
  useEffect(() => {
    const body = chatBodyRef.current;
    const bar = body?.querySelector<HTMLElement>(".thread-alerts");
    if (!body) return;
    const update = () => body.style.setProperty("--alerts-h", `${bar?.offsetHeight ?? 0}px`);
    update();
    if (!bar || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(update);
    observer.observe(bar);
    return () => observer.disconnect();
  }, [hasAlerts]);
  // Each press goes to the next thread of that kind, switching to its month tab when needed
  const jumpTo = (flag: "overdue" | "new") => {
    const targets = activeMessages.filter((message) => threadStatuses.get(message.id)?.[flag === "overdue" ? "overdue" : "isNew"]);
    if (!targets.length) return;
    const target = targets[jumpIndex.current[flag] % targets.length];
    jumpIndex.current[flag] += 1;
    const scroll = () => {
      const thread = messageListRef.current?.querySelector<HTMLElement>(`[data-thread-id="${target.id}"]`);
      if (!thread) return;
      thread.scrollIntoView?.({ behavior: "smooth", block: "center" });
      thread.classList.add("is-flashing");
      setTimeout(() => thread.classList.remove("is-flashing"), 1600);
    };
    if (tabOf(target) === selectedMonth) {
      scroll();
    } else {
      setMonthFilter(tabOf(target));
      setTimeout(scroll, 0);
    }
  };

  return (
    <div className={`chat-container${docked ? " is-docked" : ""}`}>
      {!docked && (
      <div className="chat-header">
        <div className="chat-header-copy">
          <h2 className="chat-header-title">{updatedReport.residentName}</h2>
          <span className="chat-header-subtitle">
            {payerPath && (
              <>
                <a
                  className="chat-header-link"
                  href={payerPath}
                  onClick={(event) => {
                    event.preventDefault();
                    onCancel();
                    navigate(payerPath);
                  }}
                >
                  <span className="capitalize">{facilitySlug}</span> / <span className="uppercase">{payerSlug}</span>
                </a>
                {" · "}
              </>
            )}
            {updatedReport.billType}
          </span>
        </div>
        <div className="chat-header-actions">
          {onDock && (
            <button className="chat-close-btn" type="button" onClick={onDock} aria-label="Open in a chat window" title="Open in a chat window">
              <i className="fa-regular fa-window-restore" aria-hidden="true" />
            </button>
          )}
          <button className="chat-close-btn" type="button" onClick={onCancel} aria-label="Close report messages">
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </div>
      </div>
      )}

      <div className="chat-body" ref={chatBodyRef}>
        {(overdueCount > 0 || newCount > 0) && (
          <nav className="thread-alerts" aria-label="Messages needing attention">
            <span className="thread-alerts-label">Needs attention</span>
            {overdueCount > 0 && (
              <button type="button" className="thread-alert is-overdue" onClick={() => jumpTo("overdue")}>
                <i className="fa-solid fa-clock" aria-hidden="true" />
                {overdueCount} overdue
                <span className="thread-alert-go">{overdueCount > 1 ? "Next" : "Go to"}</span>
              </button>
            )}
            {newCount > 0 && (
              <button type="button" className="thread-alert is-new" onClick={() => jumpTo("new")}>
                <i className="fa-solid fa-envelope" aria-hidden="true" />
                {newCount} new
                <span className="thread-alert-go">{newCount > 1 ? "Next" : "Go to"}</span>
              </button>
            )}
          </nav>
        )}
        <section className="paid-summary" aria-label="Paid claims">
          <div className="paid-summary-heading">
            <h2>Paid claims</h2>
            <span>{paidMonths.length}</span>
            <button
              className="paid-add-btn"
              type="button"
              aria-label="Add payment"
              aria-expanded={isPaymentFormOpen}
              aria-controls="payment-form"
              onClick={() => setIsPaymentFormOpen((isOpen) => !isOpen)}
            >
              <i className="fa-solid fa-plus" aria-hidden="true" />
              <span aria-hidden="true">{isPaymentFormOpen ? "Cancel" : "Add payment"}</span>
            </button>
          </div>
            <div className="paid-summary-list">
              {!paidMonths.length && <span className="paid-summary-empty">No paid months recorded.</span>}
              {paidMonths.map(([month, rawBalance]) => {
                const balance = rawBalance as AgingReportBalance;
                return (
                  <div className="paid-summary-row" key={month}>
                    <strong className="paid-summary-month">{toMonthYear(month)}</strong>
                    <div className="paid-summary-detail">
                      <span className="paid-summary-label">Paid date</span>
                      <span>{balance.paidDate ? toPrettyDate(balance.paidDate) : 'N/A'}</span>
                    </div>
                    <div className="paid-summary-detail">
                      <span className="paid-summary-label">Check #</span>
                      <span>{balance.checkNumber || 'N/A'}</span>
                    </div>
                    <button
                      className="paid-remove-btn"
                      type="button"
                      title={`Remove paid status for ${toMonthYear(month)}`}
                      aria-label={`Remove paid status for ${toMonthYear(month)}`}
                      onClick={() => onRemovePaidStatus(month)}
                    >
                      <i className="fa-solid fa-trash" aria-hidden="true" />
                    </button>
                  </div>
                );
              })}
            </div>
          <div
            id="payment-form"
            className={`collapsible-form-content${isPaymentFormOpen ? ' is-open' : ''}`}
            aria-hidden={!isPaymentFormOpen}
          >
            <div className="collapsible-form-content-inner">
              <div className="payment-form">
                <label className="payment-field">
                  <span>Month(s)</span>
                  <Select
                    aria-label="Month"
                    classNamePrefix="message-select"
                    isSearchable={false}
                    isMulti
                    menuPlacement="auto"
                    menuPosition="fixed"
                    menuPortalTarget={document.body}
                    styles={monthSelectStyles}
                    options={paymentMonthOptions}
                    placeholder="Select months..."
                    value={paymentMonthOptions.filter((option) => paymentMonths.includes(option.value))}
                    onChange={(options: readonly { value: string; label: string }[]) =>
                      setPaymentValue('paidMonths', options.map((option) => option.value))
                    }
                  />
                </label>
                <label className="payment-field">
                  <span>Pay date *</span>
                  <input
                    type="date"
                    {...registerPayment('paidDate', { required: 'Pay date is required' })}
                  />
                </label>
                <label className="payment-field">
                  <span>Check #</span>
                  <input
                    type="text"
                    {...registerPayment('checkNumber')}
                    placeholder="Optional"
                  />
                </label>
                <button className="send-btn payment-save-btn" onClick={handlePaymentSubmit(onSubmitPayment, () => setIsPaymentFormOpen(true))} type="button">
                  Save payment
                </button>
              </div>
            </div>
          </div>
          {paymentErrors.paidDate && <p className="help is-danger">{paymentErrors.paidDate.message}</p>}
        </section>
        {monthTabs.length > 0 && (
          <div
            className="month-tabs"
            ref={tabsRef}
            role="tablist"
            aria-label="Filter messages by month"
            onKeyDown={(event) => {
              const keys = [...monthTabs.map((tab) => tab.month), NEW_THREAD_TAB];
              const current = keys.indexOf(selectedMonth as string);
              const last = keys.length - 1;
              const next = { ArrowRight: Math.min(current + 1, last), ArrowLeft: Math.max(current - 1, 0), Home: 0, End: last }[event.key];
              if (next === undefined) return;
              event.preventDefault();
              setMonthFilter(keys[next]);
              event.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]')[next]?.focus();
            }}
          >
            {monthTabs.map(({ month, total, overdue, fresh, paid }) => {
              const counts = [`${total} ${total === 1 ? "message" : "messages"}`, overdue > 0 && `${overdue} overdue`, fresh > 0 && `${fresh} new`, paid && "paid"].filter(Boolean).join(", ");
              return (
              <button
                type="button"
                role="tab"
                key={month}
                id={tabId(month)}
                className={clsx("month-tab", { "has-overdue": overdue > 0, "has-paid": overdue === 0 && paid, "has-new": overdue === 0 && !paid && fresh > 0 })}
                aria-selected={selectedMonth === month}
                aria-controls="month-tab-panel"
                tabIndex={selectedMonth === month ? 0 : -1}
                onClick={() => setMonthFilter(month)}
              >
                <span className="month-tab-label" title={month.split(",").length > 3 ? month.split(",").map((value) => toMonthYear(value)).join(", ") : undefined}>{tabLabel(month)}</span>
                <span className="month-tab-count" title={counts} aria-label={counts}>
                  {total}
                </span>
              </button>
              );
            })}
            <button
              type="button"
              role="tab"
              id={tabId(NEW_THREAD_TAB)}
              className="month-tab month-tab-new"
              aria-label="New thread"
              title="New thread"
              aria-selected={isNewThreadTab}
              aria-controls="month-tab-panel"
              tabIndex={isNewThreadTab ? 0 : -1}
              onClick={() => setMonthFilter(NEW_THREAD_TAB)}
            >
              <i className="fa-solid fa-plus" aria-hidden="true" />
            </button>
          </div>
        )}
        <section
          className="message-list"
          aria-label="Report messages"
          ref={messageListRef}
          {...(monthTabs.length > 0 ? { id: "month-tab-panel", role: "tabpanel", "aria-labelledby": tabId(selectedMonth) } : {})}
        >
          {isNewThreadTab ? (
            <ReplyComposer
              report={updatedReport}
              users={users}
              monthOptions={monthOptions}
              refreshReport={refreshReport}
              onSent={(months) => setMonthFilter([...months].sort().join(","))}
            />
          ) : !activeMessages.length ? (
            <div className="no-messages">
              <i className="fa-regular fa-comments" aria-hidden="true" />
              <strong>{archivedMessages.length ? "No active messages" : "No messages yet"}</strong>
              <span>{archivedMessages.length ? "Archived conversations can be viewed below." : "Start the conversation below."}</span>
              <ReplyComposer report={updatedReport} users={users} monthOptions={monthOptions} refreshReport={refreshReport} />
            </div>
          ) : (
            visibleMessages.map((message) =>
              <ReportMessage key={message.id} message={message} status={threadStatuses.get(message.id)} displayNames={displayNames} parentReport={report} users={users} monthOptions={monthOptions} refreshReport={refreshReport} />
            )
          )}
          {archivedMessages.length > 0 && (
            <div className="archived-messages">
              <button
                type="button"
                className="archived-messages-toggle"
                aria-expanded={showArchivedMessages}
                onClick={() => setShowArchivedMessages((isOpen) => !isOpen)}
              >
                <span>
                  <i className="fa-solid fa-box-archive" aria-hidden="true" />
                  {showArchivedMessages ? "Hide" : "View"} archived conversations ({archivedMessages.length})
                </span>
                <i className={`fa-solid ${showArchivedMessages ? "fa-chevron-up" : "fa-chevron-down"}`} aria-hidden="true" />
              </button>
              {showArchivedMessages && archivedMessages.map((message) => (
                <ReportMessage
                  key={message.id}
                  message={message}
                  displayNames={displayNames}
                  parentReport={report}
                  refreshReport={refreshReport}
                  isArchived
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
